<template>
  <Dialog :visible="visible" modal :header="t('billing.contact_sales.title')" :style="{ width: '26rem' }"
    :dismissableMask="true" @update:visible="(v: boolean) => emit('update:visible', v)">
    <div class="flex flex-col gap-4">
      <p class="text-surface-500 m-0">
        {{ t(subtitleKey) }}
      </p>

      <div v-if="contact?.name || contact?.email || contact?.phone" class="flex flex-col gap-2">
        <!-- MYAC-1348 — the person behind the contact, as the help dialog showed them
             before the help button moved to the support chat. -->
        <div v-if="showPhoto || contact?.name" class="flex items-center gap-3" data-testid="contact-sales-person">
          <Avatar v-if="showPhoto" :image="contact?.photo_url" shape="circle" size="xlarge" />
          <div v-if="contact?.name" class="flex flex-col">
            <span class="font-medium">{{ contact.name }}</span>
            <span v-if="contact.role" class="text-sm text-surface-500">{{ contact.role }}</span>
          </div>
        </div>
        <a v-if="contact?.email" :href="`mailto:${contact.email}`"
          class="flex items-center gap-2 text-primary no-underline hover:underline">
          <i class="pi pi-envelope" />
          <span>{{ contact.email }}</span>
        </a>
        <a v-if="contact?.phone" :href="`tel:${contact.phone}`"
          class="flex items-center gap-2 text-primary no-underline hover:underline">
          <i class="pi pi-phone" />
          <span>{{ contact.phone }}</span>
        </a>
      </div>

      <!-- MYAC-1348 — the support chat, for whoever would rather write than call.
           Shown only where the chat can open: the email and phone above stay the
           way to reach a person everywhere else. -->
      <Button v-if="supportChatAvailable" :label="t('help.support_chat.contact_support')" icon="pi pi-comments"
        outlined class="self-start" data-testid="contact-sales-support-chat" @click="contactSupport" />
    </div>

    <template #footer>
      <Button text severity="secondary" :label="t('shared.close')"
        @click="emit('update:visible', false)" />
      <Button :label="t('billing.contact_sales.cta')" icon="pi pi-external-link"
        as="a" :href="contactSalesUrl" :target="contactSalesIsHttp ? '_blank' : undefined"
        :rel="contactSalesIsHttp ? 'noopener noreferrer' : undefined"
        @click="emit('update:visible', false)" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import { useAppStore } from '@/stores/app'
import { useSupportChat } from '@/composables/useSupportChat'
import { DEFAULT_CONTACT_SALES_URL } from '@/constants/billing'
import type { HelpContactConfig } from '@/models/PublicConfig'
import type { ContactSalesScope } from '@/composables/useSelfServeGate'

const props = defineProps<{ visible: boolean; scope?: ContactSalesScope }>()
const emit = defineEmits<{ 'update:visible': [value: boolean] }>()

const { t } = useI18n()
const appStore = useAppStore()

// The sales/CSM contact reuses the existing help_contact config; contact_sales_url
// is an optional external CTA (contact form / booking link).
const contact = computed<HelpContactConfig | undefined>(() => appStore.publicConfig?.help_contact)
const showPhoto = computed(() => Boolean(contact.value?.show_photo && contact.value?.photo_url))
const { isAvailable: supportChatAvailable, open: openSupportChat } = useSupportChat()

// The dialog closes first: its modal mask would otherwise sit over the chat. The
// button only shows when the chat is configured, so a false here means the vendor
// script failed at click time: say so rather than do nothing.
async function contactSupport() {
  emit('update:visible', false)
  if (await openSupportChat()) return
  appStore.toast(t('help.support_chat.unavailable_title'), t('help.support_chat.unavailable_detail'), 'warn')
}
// Always resolve to a destination (config value, else the shared default) so the
// CTA never renders as a dead-end. mailto:/tel: must stay same-tab.
const contactSalesUrl = computed<string>(() => appStore.contactSalesUrl || DEFAULT_CONTACT_SALES_URL)
const contactSalesIsHttp = computed(() => /^https?:\/\//i.test(contactSalesUrl.value))

const subtitleKey = computed(() =>
  props.scope === 'topup'
    ? 'billing.contact_sales.subtitle_topup'
    : props.scope === 'upgrade'
      ? 'billing.contact_sales.subtitle_upgrade'
      : props.scope === 'connect_td'
        ? 'billing.contact_sales.subtitle_connect_td'
        : 'billing.contact_sales.subtitle'
)

watch(
  () => props.visible,
  (open) => {
    if (open) appStore.loadPublicConfig()
  }
)
</script>
